/**
 * The application shell: navigation, the top bar, and the banners that sit
 * above every page.
 *
 * English is the source of truth for the shape — Turkish is typed as
 * `typeof en`, so a key added on one side and forgotten on the other is a
 * compile error rather than an English string appearing mid-sentence in a
 * Turkish screen.
 */
const en = {
  groups: {
    core: 'Core',
    analytics: 'Analytics',
    admin: 'Admin',
    platform: 'Platform Admin',
  },
  nav: {
    dashboard: 'Dashboard',
    live: 'Live',
    sessions: 'Sessions',
    tickets: 'Tickets',
    signals: 'Signals',
    errors: 'Errors',
    alerts: 'Alerts',
    summaries: 'AI Summaries',
    ask: 'Ask',
    supportAnalytics: 'Support Analytics',
    userBehavior: 'User Behavior',
    funnel: 'Conversion Funnel',
    heatmaps: 'Heatmaps',
    users: 'User Management',
    apiKeys: 'API Keys',
    apiAccess: 'API & MCP access',
    sourcemaps: 'Sourcemaps',
    integrations: 'Integrations Hub',
    ai: 'AI & Models',
    configuration: 'Configuration',
    security: 'Security',
    recording: 'Who gets recorded',
    billing: 'Billing',
    help: 'Help',
    onboarding: 'Onboarding',
    companies: 'Companies',
    platformUsers: 'Users',
    plans: 'Plans',
    email: 'Email',
    demoRequests: 'Demo requests',
    recaptcha: 'reCAPTCHA',
  },
  bar: {
    openNav: 'Open navigation',
    search: 'Search tickets, sessions…',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    logOut: 'Log out',
    language: 'Language',
  },
  impersonation: {
    /** Split so the organization name can be emphasised in place. */
    prefix: 'Super-admin view. You are seeing the dashboard as',
    exit: 'Exit company view',
  },
  verifyBanner: {
    body: 'Please verify your email address. We sent a confirmation link to',
    bodyTail: '. Check your inbox to activate all features.',
    resend: 'Resend email',
    sending: 'Sending…',
    sent: 'Sent ✓',
  },
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    empty: 'You’re all caught up.',
    viewAll: 'View all alerts',
  },
  limits: {
    title: 'Package limit',
    notice: (metric: string, level: number) =>
      ({
        sessions: level >= 100 ? 'This month’s recording quota is used up.' : 'This month’s recording quota is 80% used.',
        projects: level >= 100 ? 'All projects in your package are in use.' : 'Your package’s projects are 80% in use.',
        integrations:
          level >= 100 ? 'All integration tools in your package are in use.' : 'Your package’s integration tools are 80% in use.',
      })[metric] ?? 'A package limit was reached.',
    banner: (metric: string, level: number, used: string, limit: string) =>
      ({
        sessions:
          level >= 100
            ? `This month’s recording quota is used up (${used} / ${limit}). New sessions are not recorded until the month ends or an add-on is added.`
            : `This month’s recorded sessions: ${used} / ${limit}. Recording stops at the limit.`,
        projects:
          level >= 100
            ? `All ${limit} projects in your package are in use. A new project needs an add-on.`
            : `Projects in use: ${used} / ${limit}.`,
        integrations:
          level >= 100
            ? `All ${limit} integration tools in your package are in use. A new tool needs an add-on.`
            : `Integration tools in use: ${used} / ${limit}.`,
      })[metric] ?? '',
    cta: 'See usage',
  },
  license: {
    expiring: (days: number, date: string) => `This installation's licence ends in ${days} day${days === 1 ? '' : 's'}, on ${date}. Ask for a renewal to keep recording.`,
    none: (days: number, date: string) =>
      `This installation has no licence. It runs as Startup until ${date} (${days} day${days === 1 ? '' : 's'}); then new sessions stop being recorded. Set REPLAYOPS_LICENSE.`,
    invalid: (reason: string, days: number, date: string) =>
      `The licence is not valid (${reason}). Recording continues until ${date} (${days} day${days === 1 ? '' : 's'}), then stops. Check REPLAYOPS_LICENSE.`,
    expired: (ended: string, days: number, date: string) =>
      `The licence ended on ${ended}. Recording continues until ${date} (${days} day${days === 1 ? '' : 's'}), then stops. Ask for a renewal.`,
    stopped:
      'New sessions are not being recorded: this installation has no valid licence. Recorded sessions and every screen stay available.',
  },
};

const tr: typeof en = {
  groups: {
    core: 'Temel',
    analytics: 'Analitik',
    admin: 'Yönetim',
    platform: 'Platform Yönetimi',
  },
  nav: {
    dashboard: 'Panel',
    live: 'Canlı',
    sessions: 'Oturumlar',
    tickets: 'Talepler',
    signals: 'Sinyaller',
    errors: 'Hatalar',
    alerts: 'Uyarılar',
    summaries: 'AI Özetleri',
    ask: 'Sor',
    supportAnalytics: 'Destek Analitiği',
    userBehavior: 'Kullanıcı Davranışı',
    funnel: 'Dönüşüm Hunisi',
    heatmaps: 'Isı Haritaları',
    users: 'Kullanıcı Yönetimi',
    apiKeys: 'API Anahtarları',
    apiAccess: 'API ve MCP erişimi',
    sourcemaps: 'Kaynak Haritaları',
    integrations: 'Entegrasyonlar',
    ai: 'AI ve Modeller',
    configuration: 'Yapılandırma',
    security: 'Güvenlik',
    recording: 'Kimler kaydedilsin',
    billing: 'Faturalandırma',
    help: 'Yardım',
    onboarding: 'Başlangıç',
    companies: 'Şirketler',
    platformUsers: 'Kullanıcılar',
    plans: 'Planlar',
    email: 'E-posta',
    demoRequests: 'Demo talepleri',
    recaptcha: 'reCAPTCHA',
  },
  bar: {
    openNav: 'Menüyü aç',
    search: 'Talep, oturum ara…',
    darkMode: 'Koyu tema',
    lightMode: 'Açık tema',
    logOut: 'Çıkış yap',
    language: 'Dil',
  },
  impersonation: {
    prefix: 'Süper yönetici görünümü. Paneli şu şirket olarak görüyorsunuz:',
    exit: 'Şirket görünümünden çık',
  },
  verifyBanner: {
    body: 'Lütfen e-posta adresinizi doğrulayın. Onay bağlantısını şu adrese gönderdik:',
    bodyTail: '. Tüm özellikleri açmak için gelen kutunuza bakın.',
    resend: 'E-postayı yeniden gönder',
    sending: 'Gönderiliyor…',
    sent: 'Gönderildi ✓',
  },
  notifications: {
    title: 'Bildirimler',
    markAllRead: 'Tümünü okundu işaretle',
    empty: 'Her şey güncel.',
    viewAll: 'Tüm uyarıları gör',
  },
  limits: {
    title: 'Paket sınırı',
    notice: (metric: string, level: number) =>
      ({
        sessions: level >= 100 ? 'Bu ayın kayıt kotası doldu.' : 'Bu ayın kayıt kotasının %80 kadarı kullanıldı.',
        projects: level >= 100 ? 'Paketinizdeki projelerin tümü kullanımda.' : 'Paketinizdeki projelerin %80 kadarı kullanımda.',
        integrations:
          level >= 100
            ? 'Paketinizdeki entegrasyon araçlarının tümü kullanımda.'
            : 'Paketinizdeki entegrasyon araçlarının %80 kadarı kullanımda.',
      })[metric] ?? 'Bir paket sınırına ulaşıldı.',
    banner: (metric: string, level: number, used: string, limit: string) =>
      ({
        sessions:
          level >= 100
            ? `Bu ayın kayıt kotası doldu (${used} / ${limit}). Ay bitene ya da ek paket eklenene kadar yeni oturum kaydedilmiyor.`
            : `Bu ay kaydedilen oturum: ${used} / ${limit}. Sınıra ulaşınca kayıt durur.`,
        projects:
          level >= 100
            ? `Paketinizdeki ${limit} projenin tümü kullanımda. Yeni proje için ek paket gerekir.`
            : `Kullanımdaki proje: ${used} / ${limit}.`,
        integrations:
          level >= 100
            ? `Paketinizdeki ${limit} entegrasyon aracının tümü kullanımda. Yeni araç için ek paket gerekir.`
            : `Kullanımdaki entegrasyon aracı: ${used} / ${limit}.`,
      })[metric] ?? '',
    cta: 'Kullanımı gör',
  },
  license: {
    expiring: (days: number, date: string) => `Bu kurulumun lisansı ${days} gün sonra, ${date} tarihinde bitiyor. Kayıt sürsün diye yenileme isteyin.`,
    none: (days: number, date: string) =>
      `Bu kurulumun lisansı yok. ${date} tarihine kadar (${days} gün) Startup olarak çalışır, sonra yeni oturum kaydı durur. REPLAYOPS_LICENSE ayarlayın.`,
    invalid: (reason: string, days: number, date: string) =>
      `Lisans geçersiz (${reason}). Kayıt ${date} tarihine kadar (${days} gün) sürer, sonra durur. REPLAYOPS_LICENSE değerini kontrol edin.`,
    expired: (ended: string, days: number, date: string) =>
      `Lisansın süresi ${ended} tarihinde doldu. Kayıt ${date} tarihine kadar (${days} gün) sürer, sonra durur. Yenileme isteyin.`,
    stopped:
      'Yeni oturumlar kaydedilmiyor: bu kurulumun geçerli bir lisansı yok. Kayıtlı oturumlar ve tüm ekranlar açık kalır.',
  },
};

export const shell = { en, tr };
